import { type ReactElement, useMemo } from 'react';
import { queryRegisterInterest } from '@/features/superuser/data/registerInterest';
import { queryKeys } from '@/shared/hooks';
import { useAccessToken } from '@/shared/hooks/auth';
import { Surface, Text } from '@/ui';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { formatDate } from '@/ui/formatters/formatDate';
import { type GridColDef } from '@/ui/primitives';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { RegisterInterestDto } from '@flowqa/shared';

export const RegisterInterestTable = (): ReactElement => {
  const accessToken = useAccessToken();
  const columns = useMemo<Array<GridColDef<RegisterInterestDto>>>(() => {
    const nameColumn: GridColDef<RegisterInterestDto> = {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      valueGetter: (_value, row) => `${row.firstName} ${row.lastName}`.trim(),
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.firstName} {params.row.lastName}
        </Text>
      ),
    };
    const emailColumn: GridColDef<RegisterInterestDto> = {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      valueGetter: (_value, row) => row.email,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.email}
        </Text>
      ),
    };
    const companyColumn: GridColDef<RegisterInterestDto> = {
      field: 'companyName',
      headerName: 'Company',
      flex: 1,
      valueGetter: (_value, row) => row.companyName ?? '',
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.companyName ?? '-'}
        </Text>
      ),
    };
    const roleColumn: GridColDef<RegisterInterestDto> = {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      valueGetter: (_value, row) => row.role ?? '',
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.role ?? '-'}
        </Text>
      ),
    };
    const sizeColumn: GridColDef<RegisterInterestDto> = {
      field: 'companySize',
      headerName: 'Company Size',
      flex: 1,
      valueGetter: (_value, row) => row.companySize ?? '',
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.companySize ?? '-'}
        </Text>
      ),
    };
    const createdAtColumn: GridColDef<RegisterInterestDto> = {
      field: 'createdAt',
      headerName: 'Registered',
      flex: 1,
      type: 'date',
      valueGetter: (_value, row) => {
        const raw = row.createdAt;
        if (raw instanceof Date) return raw;
        if (typeof raw === 'string') {
          const parsed = new Date(raw);
          return Number.isNaN(parsed.valueOf()) ? null : parsed;
        }
        return null;
      },
      renderCell: (params) => (
        <Text role="body" component="span">
          {formatDate(params.row.createdAt) || '-'}
        </Text>
      ),
    };
    return [nameColumn, emailColumn, companyColumn, roleColumn, sizeColumn, createdAtColumn];
  }, []);

  const queryFn = async (
    input: GridQueryInput,
  ): Promise<{ items: RegisterInterestDto[]; total: number; page: number; pageSize: number }> => {
    return await queryRegisterInterest(input, accessToken);
  };

  return (
    <>
      <Text role="sectionTitle">Register Interest</Text>
      <Surface padding="none" border={false} sx={{ width: '100%', minWidth: 0 }}>
        <ServerDataTable<RegisterInterestDto>
          queryPath="superuser-register-interest"
          queryKeyPrefix={queryKeys.superuserRegisterInterest()}
          queryFn={queryFn}
          columns={columns}
          getRowId={(row) => row.id}
          token={accessToken}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={5}
          autoHeight
          noRowsText="No register interest submissions"
          disableColumnMenu
        />
      </Surface>
    </>
  );
};
